import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Loader2, BookOpen, Sparkles,
  CheckCircle2, XCircle, AlertTriangle, MinusCircle,
  Lock, Unlock, History, FileText, Calendar,
} from "lucide-react";

import { LOGO_URL } from "../config/constants";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";

const TX_TYPES = [
  "Sale", "Purchase", "Remortgage", "ToE Remo", "H2B Remo",
  "Bridging ReFinance", "Auction Purchase", "Staircasing",
  "RTB Purchase", "Acting for lender", "Redemption", "Transfer", "Other",
];
const STATUS_OPTIONS = ["Invoiced", "Awaiting invoice", "Cancelled", "Other"];

const LABEL_MAP = { yes: "Yes", no: "No", na: "N/A", drafted: "Drafted", requested: "Requested" };
const SOURCE_LABELS = {
  pre_exchange: "Pre-Exchange checklist",
  pre_completion: "Pre-Completion checklist",
};

function RadioGroup({ value, onChange, options, id }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
            value === opt
              ? opt === "yes" ? "bg-teal/20 border-teal/40 text-teal"
                : opt === "no" ? "bg-status-red/20 border-red-500/40 text-status-red"
                : opt === "drafted" ? "bg-status-amber/20 border-amber-500/40 text-status-amber"
                : opt === "requested" ? "bg-blue-500/20 border-blue-500/40 text-blue-300"
                : "bg-slate-500/20 border-slate-500/40 text-slate-300"
              : "border-navy-border text-txt-muted hover:border-teal/30"
          }`}
          data-testid={`${id}-${opt}`}
        >
          {LABEL_MAP[opt] || opt}
        </button>
      ))}
    </div>
  );
}

function FieldRow({ label, required, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 items-start py-3 border-b border-navy-border/30 last:border-0">
      <Label className="text-sm font-medium text-txt md:pt-1.5">
        {label}{required && <span className="text-status-red ml-0.5">*</span>}
      </Label>
      <div>{children}</div>
    </div>
  );
}

export default function CompletionsBookForm() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const prefillMatter = searchParams.get("matter");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!editId);
  const [prefilling, setPrefilling] = useState(false);
  const [linkedPrecomp, setLinkedPrecomp] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);

  // Completion date source control
  const [dateSource, setDateSource] = useState(null); // { source, source_id, original_date }
  const [dateLocked, setDateLocked] = useState(false);
  const [dateOverrideMode, setDateOverrideMode] = useState(false);
  const [changeReason, setChangeReason] = useState("");

  const [f, setF] = useState({
    completion_date: "",
    fee_earner: "",
    client_name: "",
    property_address: "",
    completed_flag: "no",
    transaction_type: "Purchase",
    transaction_value: "",
    other_type_details: "",
    indemnities_required: "",
    signed_docs_received: "no",
    os_and_k_searches_done: "no",
    exchanged_flag: "no",
    redemption_statement_received: "na",
    completion_statement_prepared: "no",
    cot_submitted: "na",
    client_funds_requested: "no",
    status: "",
    status_notes: "",
    matterref: prefillMatter || "",
    notes: "",
    // Audit fields
    completion_date_source: "",
    completion_date_source_id: "",
    completion_date_overridden: false,
    completion_date_change_reason: "",
    completion_date_history: [],
  });

  const set = (field, value) => setF(prev => ({ ...prev, [field]: value }));

  // Fetch source date for a matter reference
  const fetchSourceDate = async (matterRef) => {
    if (!matterRef) return;
    try {
      const res = await axios.get(`${API}/completionsbook/source-date/${encodeURIComponent(matterRef)}`);
      if (res.data.found) {
        setDateSource({
          source: res.data.date_source,
          source_id: res.data.date_source_id,
          original_date: res.data.completion_date,
          pre_exchange_date: res.data.pre_exchange_date,
          pre_completion_date: res.data.pre_completion_date,
        });
        return res.data;
      }
    } catch { /* ignore */ }
    return null;
  };

  // Load existing entry for edit
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/completionsbook/${editId}`);
        const data = res.data;
        setF(prev => ({ ...prev, ...data }));

        // Check if this entry has a source
        if (data.completion_date_source) {
          setDateSource({
            source: data.completion_date_source,
            source_id: data.completion_date_source_id,
            original_date: data.completion_date,
          });
          setDateLocked(!data.completion_date_overridden);
        }

        // Also fetch fresh source date to check for updates
        if (data.matterref) {
          const sd = await fetchSourceDate(data.matterref);
          if (sd) {
            // If entry already has a source, keep it locked unless overridden
            if (!data.completion_date_overridden) {
              setDateLocked(true);
            }
          }
        }

        // Look up linked pre-completion
        setLinkLoading(true);
        try {
          const linkRes = await axios.get(`${API}/completionsbook/${editId}/linked-precompletion`);
          setLinkedPrecomp(linkRes.data);
        } catch { /* ignore */ }
        setLinkLoading(false);
      } catch { toast.error("Could not load entry"); navigate("/completions-book"); }
      setLoading(false);
    })();
  }, [editId, navigate]);

  // Prefill from pre-completion (new entries)
  useEffect(() => {
    if (!prefillMatter || editId) return;
    (async () => {
      setPrefilling(true);
      try {
        const res = await axios.get(`${API}/completionsbook/prefill/${encodeURIComponent(prefillMatter)}`);
        if (res.data.found) {
          setF(prev => ({ ...prev, ...res.data.prefill }));
          if (res.data.date_source) {
            setDateSource({
              source: res.data.date_source,
              source_id: res.data.date_source_id,
              original_date: res.data.prefill.completion_date,
            });
            setDateLocked(true);
          }
          toast.success("Pre-filled from checklist data");
        }
      } catch { /* ignore */ }
      setPrefilling(false);
    })();
  }, [prefillMatter, editId]);

  const handleSave = async () => {
    if (!f.completion_date || !f.fee_earner || !f.client_name) {
      toast.error("Please fill in Completion Date, Fee Earner, and Client Name");
      return;
    }
    // If date was changed and there's a source, require reason
    if (dateOverrideMode && dateSource && !changeReason.trim()) {
      toast.error("Please provide a reason for changing the completion date");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...f };
      if (dateOverrideMode && changeReason.trim()) {
        payload.completion_date_overridden = true;
        payload.completion_date_change_reason = changeReason.trim();
      }
      if (editId) {
        await axios.put(`${API}/completionsbook/${editId}`, payload);
        toast.success("Entry updated");
        // Reset override mode after successful save
        if (dateOverrideMode) {
          setDateOverrideMode(false);
          setDateLocked(true);
          setChangeReason("");
        }
      } else {
        const res = await axios.post(`${API}/completionsbook`, payload);
        toast.success("Entry created");
        navigate(`/completions-book/${res.data.id}`);
        return;
      }
    } catch (err) {
      toast.error(errorMessage(err, "Save failed"));
    }
    setSaving(false);
  };

  const handleUnlockDate = () => {
    setDateOverrideMode(true);
    setDateLocked(false);
  };

  const handleCancelOverride = () => {
    // Revert to original date
    if (dateSource?.original_date) {
      set("completion_date", dateSource.original_date);
    }
    setDateOverrideMode(false);
    setDateLocked(true);
    setChangeReason("");
  };

  if (loading) {
    return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>;
  }

  const history = f.completion_date_history || [];

  return (
    <div className="min-h-screen bg-navy">
      <nav className="sticky top-0 z-30 glass-nav border-b border-[rgba(99,102,241,0.25)]">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai" className="h-7" />
            <span className="text-white font-bold text-sm">{editId ? "Edit" : "New"} Completions Book Entry</span>
          </div>
          <Button variant="ghost" onClick={() => navigate("/completions-book")} className="text-txt-muted hover:text-teal">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-6 animate-fade-in-up space-y-4">
        {prefilling && (
          <div className="glass-card p-3 flex items-center gap-2 text-teal text-sm">
            <Sparkles className="w-4 h-4" /> Pre-filling from checklist data...
          </div>
        )}

        {/* Matter & Header */}
        <div className="glass-card p-5" data-testid="cb-header-section">
          <h3 className="text-sm font-bold text-teal mb-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Completion Details
          </h3>
          <div className="space-y-0">
            {/* Completion Date — with source control */}
            <FieldRow label="Completion Date" required>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {dateLocked ? (
                    <>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[rgba(99,102,241,0.15)] border border-[rgba(99,102,241,0.3)] max-w-xs">
                        <Lock className="w-3.5 h-3.5 text-teal flex-shrink-0" />
                        <span className="text-white text-sm font-medium" data-testid="cb-completion-date-locked">{f.completion_date}</span>
                      </div>
                      <button
                        type="button"
                        onClick={handleUnlockDate}
                        className="text-xs text-status-amber hover:text-status-amber flex items-center gap-1 px-2 py-1 rounded border border-amber-500/30 hover:bg-status-amber/10 transition-colors"
                        data-testid="cb-change-date-btn"
                      >
                        <Unlock className="w-3 h-3" /> Change date
                      </button>
                    </>
                  ) : (
                    <Input
                      type="date"
                      value={f.completion_date}
                      onChange={e => set("completion_date", e.target.value)}
                      className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white max-w-xs"
                      data-testid="cb-completion-date"
                    />
                  )}
                </div>
                {/* Source label */}
                {dateSource && !dateOverrideMode && (
                  <p className="text-[11px] text-txt-muted flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    From {SOURCE_LABELS[dateSource.source] || dateSource.source}
                    {f.completion_date_overridden && <span className="text-status-amber font-medium ml-1">(overridden)</span>}
                  </p>
                )}
                {/* Override mode: reason required */}
                {dateOverrideMode && (
                  <div className="space-y-2 p-3 rounded-md bg-status-amber/5 border border-amber-500/20" data-testid="cb-date-override-panel">
                    <p className="text-xs text-status-amber flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Reason for changing completion date after exchange <span className="text-status-red">*</span>
                    </p>
                    <Textarea
                      value={changeReason}
                      onChange={e => setChangeReason(e.target.value)}
                      placeholder="e.g. Completion date moved by mutual agreement due to chain delay..."
                      rows={2}
                      className="bg-[rgba(26,37,71,0.85)] border-amber-500/30 text-white placeholder:text-txt-muted text-xs"
                      data-testid="cb-date-change-reason"
                    />
                    <button
                      type="button"
                      onClick={handleCancelOverride}
                      className="text-[11px] text-txt-muted hover:text-white"
                      data-testid="cb-cancel-date-override"
                    >
                      Cancel — revert to original date
                    </button>
                  </div>
                )}
              </div>
            </FieldRow>
            <FieldRow label="Fee Earner" required>
              <Input value={f.fee_earner} onChange={e => set("fee_earner", e.target.value)} placeholder="e.g. RM, ADL, HD" className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted max-w-xs" data-testid="cb-fee-earner" />
            </FieldRow>
            <FieldRow label="Client Name" required>
              <Input value={f.client_name} onChange={e => set("client_name", e.target.value)} placeholder="Client name" className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="cb-client-name" />
            </FieldRow>
            <FieldRow label="Property Address">
              <Input value={f.property_address} onChange={e => set("property_address", e.target.value)} placeholder="Property address" className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="cb-property-address" />
            </FieldRow>
            <FieldRow label="Matter Reference">
              {/* Brief #4 — SmartClientMatterSearch rollout (fee-earner-
                  scoped per Block 3 table row #1; the component does not
                  expose a scoping prop, so scoping intent is captured
                  here as code intent — follow-up commission required if
                  scope filtering becomes a behavioural requirement). */}
              <SmartClientMatterSearch
                label={null}
                placeholder="e.g. TLVEC.004.6403"
                initialValue={f.matterref}
                onSelect={(result) => {
                  const ref = result.matter_reference || result.label || "";
                  set("matterref", ref);
                  if (!f.client_name && result.client_name) set("client_name", result.client_name);
                }}
                className="max-w-xs"
              />
            </FieldRow>
            <FieldRow label="Transaction Type" required>
              <Select value={f.transaction_type} onValueChange={v => set("transaction_type", v)}>
                <SelectTrigger className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white max-w-xs" data-testid="cb-tx-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TX_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {f.transaction_type === "Other" && (
                <Input value={f.other_type_details} onChange={e => set("other_type_details", e.target.value)} placeholder="Specify type..." className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted mt-2 max-w-xs" data-testid="cb-other-type" />
              )}
            </FieldRow>
            <FieldRow label="Transaction Value">
              <Input value={f.transaction_value} onChange={e => set("transaction_value", e.target.value)} placeholder="e.g. £350,000" className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted max-w-xs font-mono" data-testid="cb-transaction-value" />
              <p className="text-xs text-txt-muted mt-1">Auto-populated from Pre-Exchange checklist. Edit if needed.</p>
            </FieldRow>
            <FieldRow label="Completed">
              <RadioGroup value={f.completed_flag} onChange={v => set("completed_flag", v)} options={["yes", "no"]} id="cb-completed" />
            </FieldRow>
          </div>
        </div>

        {/* Completion Date Change History */}
        {history.length > 0 && (
          <div className="glass-card p-4" data-testid="cb-date-history">
            <h3 className="text-xs font-bold text-txt-muted mb-3 flex items-center gap-2 uppercase tracking-wider">
              <History className="w-3.5 h-3.5 text-status-amber" /> Completion Date Change History
            </h3>
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="p-2.5 rounded-md bg-navy-card border border-navy-border text-xs" data-testid={`cb-date-history-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-txt-muted">{h.old_date}</span>
                    <span className="text-txt-muted">&rarr;</span>
                    <span className="text-white font-medium">{h.new_date}</span>
                    {h.changed_by && <span className="text-txt-muted">by {h.changed_by}</span>}
                    {h.changed_at && <span className="text-txt-muted ml-auto">{new Date(h.changed_at).toLocaleDateString("en-GB")}</span>}
                  </div>
                  {h.reason && <p className="text-txt-muted italic">{h.reason}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pre-Completion Link (edit mode only) */}
        {editId && (
          <div className="glass-card p-4" data-testid="cb-precomp-link">
            {linkLoading ? (
              <div className="flex items-center gap-2 text-txt-muted text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Looking up Pre-Completion checklist...</div>
            ) : linkedPrecomp?.found ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-teal" />
                  <span className="text-sm text-white">Pre-Completion checklist found for this matter</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (linkedPrecomp.type === "checklist") navigate(`/pre-completion-checklist?id=${linkedPrecomp.id}`);
                    else navigate(`/submission/${linkedPrecomp.id}`);
                  }}
                  className="border-navy-border text-teal hover:bg-navy-hover"
                  data-testid="cb-view-precomp-btn"
                >
                  View Pre-Completion Checklist
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-sm text-txt-muted">No Pre-Completion checklist found for this matter</span>
                {f.matterref && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/pre-completion-checklist`)}
                    className="border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white"
                    data-testid="cb-start-precomp-btn"
                  >
                    Start Pre-Completion Checklist
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Status Fields */}
        <div className="glass-card p-5" data-testid="cb-status-section">
          <h3 className="text-sm font-bold text-teal mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Status Tracking
          </h3>
          <div className="space-y-0">
            <FieldRow label="Signed Docs Received">
              <RadioGroup value={f.signed_docs_received} onChange={v => set("signed_docs_received", v)} options={["yes", "no"]} id="cb-signed-docs" />
            </FieldRow>
            <FieldRow label="OS1 & K17 Done">
              <RadioGroup value={f.os_and_k_searches_done} onChange={v => set("os_and_k_searches_done", v)} options={["yes", "no", "na"]} id="cb-os-k" />
            </FieldRow>
            <FieldRow label="Exchanged">
              <RadioGroup value={f.exchanged_flag} onChange={v => set("exchanged_flag", v)} options={["yes", "no"]} id="cb-exchanged" />
            </FieldRow>
            <FieldRow label="Redemption Statement Received">
              <RadioGroup value={f.redemption_statement_received} onChange={v => set("redemption_statement_received", v)} options={["yes", "no", "na"]} id="cb-redemption" />
            </FieldRow>
            <FieldRow label="Completion Statement Prepared">
              <RadioGroup value={f.completion_statement_prepared} onChange={v => set("completion_statement_prepared", v)} options={["yes", "no", "drafted"]} id="cb-comp-stmt" />
            </FieldRow>
            <FieldRow label="CoT Submitted">
              <RadioGroup value={f.cot_submitted} onChange={v => set("cot_submitted", v)} options={["yes", "no", "na"]} id="cb-cot" />
            </FieldRow>
            <FieldRow label="Client Funds Requested">
              <RadioGroup value={f.client_funds_requested} onChange={v => set("client_funds_requested", v)} options={["yes", "no", "requested", "na"]} id="cb-funds" />
            </FieldRow>
          </div>
        </div>

        {/* Additional Info */}
        <div className="glass-card p-5" data-testid="cb-additional-section">
          <h3 className="text-sm font-bold text-teal mb-3">Additional</h3>
          <div className="space-y-0">
            <FieldRow label="Indemnities">
              <Textarea value={f.indemnities_required} onChange={e => set("indemnities_required", e.target.value)} placeholder="Indemnities to put in place / obtain from SSL" rows={2} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="cb-indemnities" />
            </FieldRow>
            <FieldRow label="Status">
              <Select value={f.status || "none"} onValueChange={v => set("status", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white max-w-xs" data-testid="cb-status">
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {f.status === "Other" && (
                <Input value={f.status_notes} onChange={e => set("status_notes", e.target.value)} placeholder="Status details..." className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted mt-2 max-w-xs" data-testid="cb-status-notes" />
              )}
            </FieldRow>
            <FieldRow label="Notes">
              <Textarea value={f.notes} onChange={e => set("notes", e.target.value)} placeholder="Any additional notes..." rows={2} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="cb-notes" />
            </FieldRow>
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-teal hover:bg-teal-hover text-white gap-2 px-8" data-testid="cb-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : editId ? "Update Entry" : "Create Entry"}
          </Button>
        </div>
      </main>
    </div>
  );
}
